import React, { useMemo } from 'react';

import { deliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { fileUploadApis } from '@apis/next/fileUploadApis';
import d2dWhitePng from '@assets/img/icons/d2d-white.png';
import selectFiles from '@chenyuwang/select-files';
import { Alert } from '@components/Alert';
import { QueryContainer } from '@components/QueryContainer';
import { resolveDeliveryDestinationOptions } from '@data/arrivalNotice';
import { deliveryRequestStrings } from '@data/deliveryRequest';
import { secureDataStrings } from '@data/secureData';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useFormUtils } from '@hooks/useFormUtils';
import { useSearch } from '@hooks/useSearch';
import {
  DeliveryRequestListItem,
  DeliveryRequestDeliverySentFileForm,
} from '@models/next/DeliveryRequest';
import type { LocalFile } from '@models/next/FileType';
import type { MobxZodField } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import * as t from 'io-ts';
import { observer } from 'mobx-react';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    deliveryTruckCompanyOnly: '運送業者専用',
    afterDeliveryPrompt: '納品後、サイン入り受領書の写真をアップロードしてください',
    uploadReceipt: '受領書アップロード',
    changeReceipt: '写真変更',
    completeDelivery: '納品完了',
    comfirmComplete: '納品完了と送信してよろしいでしょうか？',
    comfirmSuccessMessage: '納品完了送信を受付いたしました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    maxHeight: '100dvh',
    overflow: 'scroll',
  },
  header: {
    position: 'sticky',
    top: 0,
  },
  headerLogoContainer: {
    ...common.backgroundPrimary,
    ...common.flexColumnCenter,
    height: 50,
  },
  headerTruckCompanyOnly: {
    ...common.fontWeight600,
    ...common.flexCenter,
    backgroundColor: theme.palette.neutral.content,
    height: 40,
  },
  deliveryRequestSummaryContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: 30,
    rowGap: 12,
    '& *[role="label"]': {
      ...common.fontWeight600,
    },
  },
  deliveryRequestReceiptUploadContentContainer: {
    ...common.flexColumn,
    padding: '20px 25px 100px',
  },
  afterDeliveryPrompt: {
    ...common.textBlack,
    ...common.smallerText,
    ...common.fontWeight600,
  },
  uploadButton: {
    ...common.backgroundPrimary,
    ...common.textWhite,
    ...common.middleText,
    ...common.fontWeight600,
    ...common.selfCenter,
    padding: '6px 20px',
  },
  changeButton: {
    ...common.textWhite,
    ...common.middleText,
    ...common.fontWeight600,
    ...common.selfCenter,
    backgroundColor: theme.palette.accent.focus,
    padding: '6px 6px',
  },
  submitButton: {
    ...common.backgroundPrimary,
    ...common.textWhite,
    ...common.biggerText,
    ...common.fontWeight600,
    padding: '10px 20px',
  },
  imageUploadContainer: {
    border: `1px solid ${theme.palette.grey[300]}`,
    width: '100%',
    maxWidth: 250,
    aspectRatio: '1',
  },
  imageUploadButtonContainer: {
    ...common.flexCenter,
    height: '100%',
    width: '100%',
  },
  imageUploadPreview: {
    height: '100%',
    width: '100%',
    display: 'block',
    objectFit: 'cover',
  },
  imageUploadActionsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 10,
    padding: '8px 10px',
    marginTop: 22,
  },
  completeDeliveryContainer: {
    ...common.flexColumn,
    marginTop: 30,
    marginBottom: 30,
  },
  errorContainer: {
    padding: '1rem',
  },
}));

const searchType = t.type({
  delivery_request: t.string,
  file_upload_access: t.string,
  complete_delivery_request_access: t.string,
  is_complete: h.omittable(h.boolean().booleanish()),
});

const Header = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.header}>
      <div className={classes.headerLogoContainer}>
        <img src={d2dWhitePng} style={{ height: 20, width: 86 }} />
      </div>
      <div className={classes.headerTruckCompanyOnly}>{strings.deliveryTruckCompanyOnly}</div>
    </div>
  );
};

const ImageUpload: React.FC<{ field: MobxZodField<typeof LocalFile> }> = observer(({ field }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);

  const onClickUpload = async () => {
    const fileList = [...((await selectFiles({ accept: 'image/*', multiple: false })) ?? [])];

    if (fileList.length === 0) return;

    field.setOutput({ file: fileList[0], remote: undefined });
  };

  const fileUrl = useMemo(
    () => field.decodeResult.success && URL.createObjectURL(field.decodeResult.data.file),
    [field.decodeResult.success && field.decodeResult.data],
  );

  return (
    <>
      <div role="label">
        <div>{deliveryRequestStrings.receiptPhoto}</div>
        {field.decodeResult.success && !search.is_complete && (
          <button
            className={classes.changeButton}
            style={{ marginTop: 16 }}
            type="button"
            onClick={onClickUpload}
          >
            {strings.changeReceipt}
          </button>
        )}
      </div>
      <div>
        <div className={classes.imageUploadContainer}>
          {!field.decodeResult.success && (
            <div className={classes.imageUploadButtonContainer}>
              <button className={classes.uploadButton} onClick={onClickUpload}>
                {strings.uploadReceipt}
              </button>
            </div>
          )}
          {fileUrl && (
            <img
              src={fileUrl}
              alt={deliveryRequestStrings.deliverySentFile}
              className={classes.imageUploadPreview}
            />
          )}
        </div>
      </div>
    </>
  );
});

const DeliveryRequestReceiptUploadContent: React.FC<{
  deliveryRequest: DeliveryRequestListItem;
}> = ({ deliveryRequest }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(searchType);

  const fileUpload = fileUploadApis.usePost('/file/upload', {
    queries: {
      file_upload_access: search.file_upload_access,
    },
  });

  const putDeliverySent = deliveryRequestApis.usePut(
    '/delivery-request/delivery-sent/:secure_data_key',
    {
      params: { secure_data_key: search.complete_delivery_request_access },
    },
  );

  const form = useForm(DeliveryRequestDeliverySentFileForm);

  const { fields } = form.root;

  const { confirm, renderDialog } = useConfirmDialog();

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [fileUpload, putDeliverySent],
  });

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          if ((await confirm({ message: strings.comfirmComplete })) === 'confirmed') {
            const [delivery_sent_file] = await fileUpload.mutateAsync({
              file: [values.delivery_sent_file.file!],
            });

            await putDeliverySent.mutateAsync({
              delivery_sent_file,
            });

            await confirm({ message: strings.comfirmSuccessMessage });

            updateSearch({ is_complete: true });
          }
        },
      })}
      className={classes.deliveryRequestReceiptUploadContentContainer}
    >
      {renderAlertsAndSpinnerModal()}
      {renderDialog()}

      <div className={classes.deliveryRequestSummaryContainer}>
        <div role="label">{deliveryRequestStrings.deliveryRequestNo}</div>
        <div>{deliveryRequest.delivery_request_no}</div>
        <div role="label">{deliveryRequestStrings.deliveryDate}</div>
        <div>{deliveryRequestStrings.formatDeliveryDate(deliveryRequest)}</div>
        <div role="label">{deliveryRequestStrings.deliverySummary}</div>
        <div>{deliveryRequestStrings.getDeliverySummary(deliveryRequest)}</div>
        <div role="label">{deliveryRequestStrings.pickupAddress}</div>
        <div>
          {resolveDeliveryDestinationOptions(deliveryRequest.pickup_address).delivery_destination}
        </div>
        <div role="label">{deliveryRequestStrings.receiver}</div>
        <div>{deliveryRequest.booking_inquiry.consignee?.company_name || '-'}</div>

        <div
          className={classes.afterDeliveryPrompt}
          style={{ paddingTop: 20, paddingBottom: 20, gridColumn: '1 / -1' }}
        >
          {strings.afterDeliveryPrompt}
        </div>

        <ImageUpload field={fields.delivery_sent_file} />
      </div>

      <div className={classes.completeDeliveryContainer}>
        <button className={classes.submitButton} type="submit">
          {strings.completeDelivery}
        </button>
      </div>
    </form>
  );
};

export const DeliveryRequestReceiptUpload: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);

  const deliveryRequestQuery = deliveryRequestApis.useGet(
    '/delivery-request/secure-data/:secure_data_key',
    {
      params: { secure_data_key: search.delivery_request },
    },
  );

  return (
    <div className={classes.container}>
      <Header />

      <QueryContainer queries={[deliveryRequestQuery]}>
        {(() => {
          if (deliveryRequestQuery.data?.state === 'ACTIVE') {
            return (
              <DeliveryRequestReceiptUploadContent
                deliveryRequest={deliveryRequestQuery.data.data}
              />
            );
          } else if (deliveryRequestQuery.data?.state === 'CANCELLED') {
            return (
              <div className={classes.errorContainer}>
                <Alert open severity="error">
                  {secureDataStrings.errorCancelled}
                </Alert>
              </div>
            );
          } else if (deliveryRequestQuery.data?.state === 'OUTDATED') {
            return (
              <div className={classes.errorContainer}>
                <Alert open severity="warning">
                  {secureDataStrings.errorOutdated}
                </Alert>
              </div>
            );
          }
        })()}
      </QueryContainer>
    </div>
  );
};
